export type InlineReactionCommentLocation = {
	__typename?: string;
	type?: string;
	inlineMarkerRef?: string | null;
};

type ReactionsSummary = {
	reactionsCount: number;
};

export type InlineReactionThreadLike = {
	parentId?: string | null;
	body?: { value: string } | null;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- typed comment field, not the browser `location` global
	location?: InlineReactionCommentLocation | null;
	reactionsSummary?: ReactionsSummary | null;
};

export const isInlineCommentLocation = (
	commentLocation?: InlineReactionCommentLocation | null,
): boolean => commentLocation?.__typename === 'InlineComment' || commentLocation?.type === 'INLINE';

export const getInlineReactionCommentLocation = (
	comment: InlineReactionThreadLike,
): InlineReactionCommentLocation | null | undefined => comment.location;

export const hasEmptyInlineReactionBody = (comment: InlineReactionThreadLike): boolean => {
	const bodyValue = comment.body?.value ?? '';
	if (bodyValue.trim() === '') {
		return true;
	}

	try {
		const adf = JSON.parse(bodyValue);
		if (adf?.type !== 'doc') {
			return false;
		}

		const content = adf?.content;
		return (
			!content ||
			content.length === 0 ||
			(content.length === 1 &&
				content[0].type === 'paragraph' &&
				(!content[0].content || content[0].content.length === 0))
		);
	} catch {
		return false;
	}
};

/**
 * Pure predicate — returns true when the comment represents a reaction-only
 * inline highlight (i.e. an anchor comment created by the
 * `confluence_addInlineReaction` mutation rather than by a user authoring an
 * inline comment).
 *
 * The four guards:
 *   1. Top-level comment (no `parentId`) — replies can carry reactions but are
 *      never reaction-only anchors.
 *   2. Located on an inline annotation (`location.__typename === 'InlineComment'`)
 *      — reactions are an inline-only feature for now.
 *   3. `reactionsSummary.reactionsCount > 0` — protects against tombstone
 *      anchor rows where every user un-reacted; rendering an empty tile
 *      would be a UX regression.
 *   4. Body is empty / whitespace-only ADF — THIS is the load-bearing signal.
 *
 * Why guard 4 exists: the backend currently uses the SAME `annotationType:
 * "inline-comments"` for both regular inline comments and reaction-only
 * anchors, so `useAllCommentsData` returns both kinds in a single list. The
 * AGG schema does NOT yet expose a per-comment discriminator on
 * `ConfluenceInlineComment` (the `ConfluenceInlineMarkerType: COMMENT |
 * REACTION` enum lives only on `ConfluenceInlineMarker`, returned by the
 * separate `confluence_activeInlineData` query). So the only client-visible
 * signal we have today is "the body is empty ADF" — that's how the backend
 * stores reaction-only anchors. Without this guard a regular commented-
 * and-reacted thread (e.g. "This is wrong" + 👍) would be misclassified as
 * a reaction tile.
 *
 * Long-term cleanup path: the inline reactions API spec calls for excluding
 * reaction-only comments from the inline-comments query and exposing a
 * proper marker `type` discriminator on the comment shape. Once
 * `ConfluenceInlineComment.commentType: ConfluenceInlineMarkerType` lands,
 * this entire predicate collapses to:
 *   `comment.commentType === 'REACTION'`
 * See: https://hello.atlassian.net/wiki/spaces/CCMF/pages/6770394134
 *
 * NOTE: This function does NOT check the `confluence_inline_reactions`
 * experiment gate. The gate is owned by the call site (currently
 * `CommentsPanelList.tsx` → `<InlineReactionThreads>`) so this util can
 * remain a pure data-shape predicate that is safe to call during tests
 * and from any future code path. Callers must guard with their own
 * gate evaluation if they need experiment-conditional behaviour.
 *
 * Linked ticket: ACTIVATION-2589
 */
export const isInlineReactionThread = (comment: InlineReactionThreadLike): boolean => {
	// Must be a root comment (no parentId)
	if (comment.parentId) {
		return false;
	}

	// Must be an inline comment location
	const commentLocation = getInlineReactionCommentLocation(comment);
	if (!isInlineCommentLocation(commentLocation)) {
		return false;
	}

	// Must have a non-empty reactions summary
	if (!comment.reactionsSummary || comment.reactionsSummary.reactionsCount <= 0) {
		return false;
	}

	return hasEmptyInlineReactionBody(comment);
};
